import React from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { StepLayout } from "#/components/carrot-plans/shared/StepLayout"
import { ButtonFooterLayout, ContinueButton, SkipButton } from "#/components/carrot-plans/shared/ButtonFooter"
import { FormattedMessage, useIntl } from "react-intl"
import { Form } from "@carrotfertility/carotene-core-x"
import { RoutingFlowTitle } from "#/components/carrot-plans/shared"
import {
  useCheckRoutingInfoQueryCache,
  hasQuestionBeenSkipped
} from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import { useMemberProviderSelectionQuery } from "#/components/carrot-plans/hooks/useMemberProviderSelection"
import { useClinicNameStepHandlers } from "#/components/carrot-plans/hooks/useMemberProviderSelectionNameSteps"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { CenteredLoadingIndicator } from "../../../views/molecules/Molecules"
import { ProviderSearchForm, ProviderSearchFormWithNoProviderOption } from "./ProviderSearchForm"
import {
  FormValues,
  PROVIDER_CONSTANTS,
  shouldShowClinicWithNoProviderOption
} from "#/components/carrot-plans/utils/providerSelectionUtils"
import { CountryCode } from "#/utils/CountryCode"
import { ProviderSelectionAction } from "#/types/memberProviderSelection"

// ============ Shared Form Rendering ============

interface ProviderFormProps {
  header: string
  onSubmit: (values: FormValues) => Promise<void>
  onSkip: () => Promise<void>
  defaultValues: FormValues
  countryCode: CountryCode
  showNoProviderOption: boolean
}

/**
 * Provider search form component with conditional "no provider" option.
 */
function ProviderForm({
  header,
  onSubmit,
  onSkip,
  defaultValues,
  countryCode,
  showNoProviderOption
}: ProviderFormProps): JSX.Element {
  return (
    <StepLayout
      header={header}
      formElements={
        <Form onSubmit={onSubmit} defaultValues={defaultValues}>
          {showNoProviderOption ? (
            <ProviderSearchFormWithNoProviderOption countryCode={countryCode} />
          ) : (
            <ProviderSearchForm countryCode={countryCode} />
          )}
          <ButtonFooterLayout
            continueButton={
              <ContinueButton>
                <FormattedMessage defaultMessage="Continue" />
              </ContinueButton>
            }
            skipButton={<SkipButton onClick={onSkip} />}
          />
        </Form>
      }
    />
  )
}

// ============ Main Entry Point ============

/**
 * Clinic name collection step. Fetches routing (useCheckRoutingInfoQueryCache) and member provider selection
 * (useMemberProviderSelectionQuery), builds form defaultValues and header, then uses useClinicNameStepHandlers
 * for submit/skip. Shows loading until the member provider selection query is ready; auto-advances if the step
 * was previously skipped or the user already has a provider name. Conditionally shows "I don't have a provider yet"
 * based on journey type, stage, user location, and fertility testing answer (see shouldShowClinicWithNoProviderOption).
 */
function NameOfClinicWithProviderSearchStep(): JSX.Element {
  const intl = useIntl()
  const routingData = useCheckRoutingInfoQueryCache()
  const routingInfoId = routingData?.routingInfoId
  const memberProviderSelectionQuery = useMemberProviderSelectionQuery(routingInfoId)
  const { company, isUsa } = useCurrentUser()

  const isProviderSelectionLoading = memberProviderSelectionQuery.isLoading || memberProviderSelectionQuery.isFetching
  const providerSelection = memberProviderSelectionQuery.data ?? null
  const showNoProviderOption = shouldShowClinicWithNoProviderOption(isUsa, routingData ?? null)
  const wasSkipped = hasQuestionBeenSkipped("nameOfClinic", routingData)
  const { createSubmitHandler, createSkipHandler } = useClinicNameStepHandlers({
    routingInfoId,
    selectionWhenReady: providerSelection,
    wasSkipped
  })

  const defaultProviderName = providerSelection?.providerEntityName || null
  const defaultNoProvider = providerSelection?.providerSelectionAction === ProviderSelectionAction.NO_PROVIDER
  const defaultProvider =
    providerSelection?.providerUuid && providerSelection?.providerEntityName
      ? {
          uuid: providerSelection.providerUuid,
          name: providerSelection.providerEntityName,
          providerEligibilityStatus: providerSelection.providerEligibilityStatusAtSelection
        }
      : null
  const defaultValues: FormValues = {
    providerName: defaultProviderName || "",
    provider: defaultProvider,
    ...(showNoProviderOption && {
      noProvider: defaultNoProvider ? PROVIDER_CONSTANTS.NO_PROVIDER_RADIO_VALUE : ""
    })
  }

  const header = showNoProviderOption
    ? intl.formatMessage({
        defaultMessage: "If you have a provider, what is the name of your fertility clinic?"
      })
    : intl.formatMessage({ defaultMessage: "What is the name of your fertility clinic?" })

  const shouldAutoAdvance = wasSkipped || !!defaultProviderName || (showNoProviderOption && defaultNoProvider)
  const { send } = useStateMachine(isProviderSelectionLoading ? undefined : shouldAutoAdvance ? "" : null)

  if (isProviderSelectionLoading) return <CenteredLoadingIndicator />
  if (!company?.countryCode) return <></>

  return (
    <>
      <RoutingFlowTitle title={header} />
      <ProviderForm
        header={header}
        onSubmit={createSubmitHandler(send)}
        onSkip={createSkipHandler(send)}
        defaultValues={defaultValues}
        countryCode={company.countryCode}
        showNoProviderOption={showNoProviderOption}
      />
    </>
  )
}

export { NameOfClinicWithProviderSearchStep }
